/**
 * Client-side Modash PDF parser using pdfjs-dist.
 *
 * SECURITY: PDF parsing runs entirely in the browser.
 * The server only receives the extracted, normalised metrics JSON.
 *
 * PDF.js integration is here; pure parsing logic lives in modashParsing.ts.
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

export type { ParsedModashReport, AudienceMetricRow } from './modashParsing';
export { parseCompactNumber, parsePercentToDecimal, parseThreeColRows, parseTwoColPercentRows, parseMultiTwoColTables } from './modashParsing';

import { parseModashText } from './modashParsing';
import type { ParsedModashReport } from './modashParsing';

// Backwards-compat alias — components that imported ParsedCreatorMetrics continue to work
export type ParsedCreatorMetrics = ParsedModashReport;

// ── PDF text extraction (line-preserving) ─────────────────────────────────

/**
 * Extract full text from a PDF, preserving line breaks.
 * Groups text items by Y-coordinate so horizontal neighbours share a line.
 *
 * Uses tolerance-based Y-grouping (±3 units) instead of exact rounding so
 * that characters like "@" that PDF.js sometimes extracts at slightly offset
 * vertical positions still land on the same line as the adjacent handle text.
 */
export async function extractPdfText(file: File | Blob): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdf         = await loadingTask.promise;

  const Y_TOLERANCE = 3;
  const pages: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();

    // Collect items with their raw Y position and X position for sorting
    const items: Array<{ str: string; x: number; y: number; hasEOL: boolean }> = [];
    for (const item of content.items) {
      if (!('str' in item)) continue;
      const typed = item as { str: string; transform: number[]; hasEOL?: boolean };
      items.push({
        str: typed.str,
        x: typed.transform[4],
        y: typed.transform[5],
        hasEOL: typed.hasEOL ?? false,
      });
    }

    // Tolerance-based Y-grouping: items within ±Y_TOLERANCE share a line.
    // Sort all items by Y descending (top of page first), then assign bins.
    const sorted = [...items].sort((a, b) => b.y - a.y);
    const bins: Array<{ representativeY: number; items: typeof items }> = [];

    for (const it of sorted) {
      const existing = bins.find(b => Math.abs(b.representativeY - it.y) <= Y_TOLERANCE);
      if (existing) {
        existing.items.push(it);
      } else {
        bins.push({ representativeY: it.y, items: [it] });
      }
    }

    // Sort bins top-to-bottom (descending Y), within each bin sort left-to-right (ascending X)
    bins.sort((a, b) => b.representativeY - a.representativeY);
    const lines = bins.map(bin => {
      bin.items.sort((a, b) => a.x - b.x);
      return bin.items.map(it => it.str).join(' ');
    });
    pages.push(lines.join('\n'));
  }

  return pages.join('\n');
}

// ── Main exported parser ──────────────────────────────────────────────────

export async function parsePdf(file: File | Blob): Promise<ParsedModashReport> {
  const rawText = await extractPdfText(file);

  // Normalise encoding artefacts before passing to text parser
  const text = rawText
    .replace(/\u00a0/g, ' ')
    .replace(/\u2013|\u2014/g, '-')
    .replace(/@ +([A-Za-z0-9_])/g, '@$1')    // collapse spaces between @ and handle on SAME line only
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\r\n?/g, '\n');

  return parseModashText(text);
}
